import { useMemo, useState } from 'react';

import { Card } from '@/components/Card';
import { Pagination } from '@/components/Pagination';
import { ColumnSelector } from '@/components/tables/TableColumnsSelector';
import { TableQueryStateContent } from '@/components/tables/TableQueryStateContent';
import { QuantileSparkline } from '@/features/forecasts/components/home/QuantileSparkline';
import { useTableUiControls } from '@/hooks/tables/useTableUiControls';
import { cls } from '@/utils/cls';

export type ForecastChangeSignalRow = {
  id: string;
  customer: string;
  sku: string;
  description: string;
  region: string;
  lastLockedP50?: string;
  delta: string;
  deltaDirection: 'up' | 'down' | 'neutral';
  deltaPercent?: string;
  p50: string;
  p10: number;
  p90: number;
  p50Raw: number;
  // Ascending P10..P90 fans for the distribution-shape sparklines (null → no data).
  lastLockedFan: number[] | null;
  currentFan: number[] | null;
  status: 'action_required' | 'monitor' | 'stable';
};

type SortKey =
  | 'customer'
  | 'sku'
  | 'region'
  | 'lastLockedP50'
  | 'p50'
  | 'delta'
  | 'deltaPercent'
  | 'status'
  | 'lastLockedShape'
  | 'currentShape';

// Shape (sparkline) columns aren't meaningfully sortable.
const NON_SORTABLE: ReadonlySet<SortKey> = new Set(['lastLockedShape', 'currentShape']);

type Filter = 'all' | 'down' | 'up' | 'action';

// Two-line column header: bold title + muted secondary line.
function ColHeader({ title, sub }: { title: string; sub: string }) {
  return (
    <span className="flex flex-col">
      <span>{title}</span>
      <span className="text-[10px] font-normal text-[#9295A5]">{sub}</span>
    </span>
  );
}

type Props = {
  rows?: ForecastChangeSignalRow[];
  pageSize?: number;
  onRowClick?: (row: ForecastChangeSignalRow) => void;
  isNavigating?: boolean;
};

const ALL_COLUMNS: ColumnDef<SortKey>[] = [
  { key: 'customer', label: 'Customer', visibleByDefault: true },
  { key: 'sku', label: 'SKU', visibleByDefault: true },
  { key: 'region', label: 'Region', visibleByDefault: true },
  {
    key: 'lastLockedP50',
    label: <ColHeader title="Last Locked Forecast" sub="FCT 04/27 · P50 · lead-time 1wk · span 1mo" />,
    visibleByDefault: true,
  },
  { key: 'lastLockedShape', label: <ColHeader title="Last Locked Shape" sub="P10–P90" />, visibleByDefault: true },
  {
    key: 'p50',
    label: <ColHeader title="Current Forecast" sub="FCT 05/04 · P50 · lead-time 1wk · span 1mo" />,
    visibleByDefault: true,
  },
  { key: 'currentShape', label: <ColHeader title="Current Shape" sub="P10–P90" />, visibleByDefault: true },
  { key: 'delta', label: <ColHeader title="Δ Units" sub="curr − last locked" />, visibleByDefault: true },
  { key: 'deltaPercent', label: <ColHeader title="Δ % Change" sub="curr / last locked − 1" />, visibleByDefault: true },
  { key: 'status', label: <ColHeader title="Flag" sub="+/− S&OP thresholds" />, visibleByDefault: true },
];

const statusLabel: Record<ForecastChangeSignalRow['status'], { label: string; cls: string }> = {
  action_required: { label: "⚠ Action req'd", cls: 'text-amber-700 bg-amber-50' },
  monitor: { label: 'Monitor', cls: 'text-blue-700 bg-blue-50' },
  stable: { label: 'Stable', cls: 'text-gray-600 bg-gray-100' },
};

export function ForecastChangeSignalsTable({ rows = [], pageSize = 10, onRowClick, isNavigating }: Props) {
  const [filter, setFilter] = useState<Filter>('all');

  const downCount = useMemo(() => rows.filter((r) => r.deltaDirection === 'down').length, [rows]);
  const upCount = useMemo(() => rows.filter((r) => r.deltaDirection === 'up').length, [rows]);
  const actionCount = useMemo(() => rows.filter((r) => r.status === 'action_required').length, [rows]);

  const filteredRows = useMemo(() => {
    if (filter === 'down') return rows.filter((r) => r.deltaDirection === 'down');
    if (filter === 'up') return rows.filter((r) => r.deltaDirection === 'up');
    if (filter === 'action') return rows.filter((r) => r.status === 'action_required');
    return rows;
  }, [rows, filter]);

  const resetKey = useMemo(() => `${filter}-${filteredRows.length}`, [filter, filteredRows.length]);

  const {
    columnSelectorOpen,
    columns,
    columnsOverlayRef,
    filteredColumns,
    page,
    setPage,
    sort,
    toggleColumn,
    toggleSort,
    visibleHeadColumns,
    openColumnSelector,
  } = useTableUiControls<SortKey>({
    allColumns: ALL_COLUMNS,
    initialSort: { key: 'status', dir: 'asc' },
    resetDeps: [resetKey],
  });

  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows];
    const dir = sort.dir === 'asc' ? 1 : -1;
    // Shape columns hold arrays and aren't sortable; read a comparable string
    // for the sortable keys and an empty string otherwise so the comparator
    // stays total even if a non-sortable key somehow slips in.
    const cell = (row: ForecastChangeSignalRow): string =>
      NON_SORTABLE.has(sort.key) ? '' : String(row[sort.key as keyof ForecastChangeSignalRow] ?? '');
    sorted.sort((a, b) => cell(a).localeCompare(cell(b), undefined, { sensitivity: 'base', numeric: true }) * dir);
    return sorted;
  }, [filteredRows, sort]);

  const totalCount = sortedRows.length;
  const pagedRows = sortedRows.slice((page - 1) * pageSize, page * pageSize);
  const globalOffset = (page - 1) * pageSize;

  const tabs: { key: Filter; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: rows.length },
    { key: 'down', label: '↓ Down', count: downCount },
    { key: 'up', label: '↑ Up', count: upCount },
    { key: 'action', label: "⚠ Action req'd", count: actionCount },
  ];

  const cellCls = 'px-3 py-3 text-sm text-gray-700 whitespace-nowrap';

  const table = (
    <div className="mt-2 flow-root">
      <div className="overflow-x-auto">
        <div className="block min-w-full">
          <table className="relative min-w-full divide-y divide-gray-300">
            <thead>
              <tr>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-400 w-8">
                  #
                </th>
                {visibleHeadColumns.map((col) => {
                  const active = sort.key === col.key;
                  const sortable = !NON_SORTABLE.has(col.key);
                  return (
                    <th key={col.key} scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {sortable ? (
                        <button
                          type="button"
                          onClick={() => toggleSort(col.key)}
                          className={cls(
                            'group inline-flex gap-1 hover:text-gray-700 cursor-pointer text-left',
                            active && 'text-gray-900',
                          )}
                        >
                          {col.label}
                        </button>
                      ) : (
                        col.label
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {pagedRows.map((row, i) => {
                const rank = globalOffset + i + 1;
                const s = statusLabel[row.status];
                return (
                  <tr
                    key={row.id}
                    className={`even:bg-gray-50 ${onRowClick ? 'cursor-pointer hover:bg-blue-50' : ''}`}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    <td className="px-3 py-3 text-xs text-gray-400 w-8">{rank}</td>
                    {filteredColumns.map((col) => {
                      if (col.key === 'sku') {
                        return (
                          <td key={`${row.id}-sku`} className={cellCls}>
                            <span className="font-medium text-gray-900">{row.sku}</span>
                            <span className="ml-1.5 text-gray-500">{row.description}</span>
                          </td>
                        );
                      }
                      if (col.key === 'delta') {
                        const colorCls =
                          row.deltaDirection === 'up'
                            ? 'text-green-600'
                            : row.deltaDirection === 'down'
                              ? 'text-red-600'
                              : 'text-gray-700';
                        return (
                          <td key={`${row.id}-delta`} className={cls(cellCls, 'font-medium', colorCls)}>
                            {row.delta}
                          </td>
                        );
                      }
                      if (col.key === 'status') {
                        return (
                          <td key={`${row.id}-status`} className={cellCls}>
                            <span
                              className={cls(
                                'inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium',
                                s.cls,
                              )}
                            >
                              {s.label}
                            </span>
                          </td>
                        );
                      }
                      if (col.key === 'lastLockedShape' || col.key === 'currentShape') {
                        // Share a y-domain across both fans so the two sparklines
                        // are directly comparable (shows shift + spread change).
                        const both = [...(row.lastLockedFan ?? []), ...(row.currentFan ?? [])];
                        const domain: [number, number] | undefined = both.length
                          ? [Math.min(...both), Math.max(...both)]
                          : undefined;
                        const fan = col.key === 'lastLockedShape' ? row.lastLockedFan : row.currentFan;
                        const color = col.key === 'lastLockedShape' ? '#9295A5' : '#274483';
                        return (
                          <td key={`${row.id}-${col.key}`} className={cellCls}>
                            <QuantileSparkline fan={fan} domain={domain} color={color} />
                          </td>
                        );
                      }
                      return (
                        <td key={`${row.id}-${col.key}`} className={cellCls}>
                          {row[col.key as keyof ForecastChangeSignalRow] as React.ReactNode}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <Card>
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Latest Forecast Compared to Last Locked</h2>
          <p className="mt-0.5 text-xs text-[#9295A5]">
            {isNavigating ? 'Loading forecast details…' : 'DFUs exceeding S&OP thresholds flagged for review'}
          </p>
        </div>
        <div className="relative shrink-0" ref={columnsOverlayRef}>
          <ColumnSelector
            columns={columns}
            open={columnSelectorOpen}
            setOpen={openColumnSelector}
            onToggle={toggleColumn}
          />
        </div>
      </div>

      {/* Filter tabs */}
      <div className="mt-3 flex gap-1 border-b border-gray-200">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setFilter(tab.key)}
            className={cls(
              'px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition-colors',
              filter === tab.key
                ? 'border-[#274483] text-[#274483]'
                : 'border-transparent text-[#9295A5] hover:text-gray-700',
            )}
          >
            {tab.label}
            <span className={cls('ml-1.5 tabular-nums', filter === tab.key ? 'text-[#274483]' : 'text-gray-400')}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      <TableQueryStateContent
        isLoading={false}
        isFetching={false}
        error={null}
        hasData={totalCount > 0}
        loadingMessage="Loading signals…"
        emptyMessage="No forecast change signals."
        errorMessage="Failed to load signals."
        table={table}
      />
      <Pagination page={page} pageSize={pageSize} setPage={setPage} totalCount={totalCount} />
    </Card>
  );
}
